import React, { useState, useEffect, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import MainLayout from './components/MainLayout';
import { trackPageView } from './services/analytics';
import ScrollToTop from './components/ScrollToTop';
import { ChildProvider } from './contexts/ChildContext';
import { AlertSettingsProvider } from './contexts/AlertSettingsContext';
import { CoursesProvider } from './contexts/CoursesContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { I18nProvider, useI18n } from './contexts/I18nContext';
import { OfflineIndicator } from './components/OfflineIndicator';
import InstallPrompt from './components/InstallPrompt';
import VersionUpdateModal from './components/VersionUpdateModal';
import Login from './components/Login';
import LoadingSpinner from './components/LoadingSpinner';
import { AppErrorBoundary } from './components/AppErrorBoundary';
import initWebViewHandler from './utils/webViewHandler';
import { versionService, VersionComparisonResult } from './services/VersionService';
import { initBackButtonHandler, destroyBackButtonHandler } from './utils/backButtonHandler';

// 路由分包：各頁面改用 lazy 載入，減少首屏 bundle 體積
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const Schedule = React.lazy(() => import('./pages/Schedule'));
const Courses = React.lazy(() => import('./pages/Courses'));
const CourseDetail = React.lazy(() => import('./pages/CourseDetail'));
const Finance = React.lazy(() => import('./pages/Finance'));
const Skills = React.lazy(() => import('./pages/Skills'));
const Settings = React.lazy(() => import('./pages/Settings'));

/** 路由變化時記錄 GA page_view */
function AnalyticsPageView() {
    const { pathname } = useLocation();
    useEffect(() => {
        const title = pathname === '/' ? 'Dashboard' : pathname === '/login' ? 'Login' : pathname.split('/').filter(Boolean).join(' / ') || pathname;
        trackPageView(pathname, title);
    }, [pathname]);
    return null;
}

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { t } = useI18n();
    const { currentUser, loading } = useAuth();

    if (loading) {
        return (
            <div className="loading-container" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: 16 }}>
                <LoadingSpinner size="lg" />
                <p style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-sm)' }}>{t('app.loading')}</p>
            </div>
        );
    }

    return currentUser ? <>{children}</> : <Navigate to="/login" />;
};

function App() {
    const [versionUpdate, setVersionUpdate] = useState<VersionComparisonResult | null>(null);
    const [showVersionModal, setShowVersionModal] = useState(false);

    useEffect(() => {
        initWebViewHandler();
    }, []);

    useEffect(() => {
        const timer = setTimeout(async () => {
            try {
                const result = await Promise.race([
                    versionService.initialize(),
                    new Promise<null>((r) => setTimeout(() => r(null), 5000)),
                ]);
                if (result?.hasUpdate) {
                    setVersionUpdate(result);
                    setShowVersionModal(true);
                }
            } catch {}
        }, 3000);
        return () => clearTimeout(timer);
    }, []);

    // 手機 PWA 返回鍵：雙擊退出（邏輯來自 TaskyVenture 簡化版）
    useEffect(() => {
        const handler = initBackButtonHandler();
        return () => {
            if (handler) {
                handler.destroy();
            } else {
                destroyBackButtonHandler();
            }
        };
    }, []);

    return (
        <AppErrorBoundary>
        <Router>
            <AnalyticsPageView />
            <ScrollToTop />
            <AuthProvider>
                <I18nProvider>
                    <ChildProvider>
                        <AlertSettingsProvider>
                            <CoursesProvider>
                                <OfflineIndicator />
                                <InstallPrompt forceInstall={false} />
                                {versionUpdate && (
                                    <VersionUpdateModal
                                        isOpen={showVersionModal}
                                        onClose={() => setShowVersionModal(false)}
                                        versionInfo={versionUpdate}
                                    />
                                )}
                                <Suspense fallback={<LoadingSpinner size="lg" />}>
                                    <Routes>
                                        <Route path="/login" element={<Login />} />
                                        <Route element={<MainLayout />}>
                                            <Route path="/" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
                                            <Route path="/schedule" element={<PrivateRoute><Schedule /></PrivateRoute>} />
                                            <Route path="/courses" element={<PrivateRoute><Courses /></PrivateRoute>} />
                                            <Route path="/courses/:childId/:courseId" element={<PrivateRoute><CourseDetail /></PrivateRoute>} />
                                            <Route path="/skills" element={<PrivateRoute><Skills /></PrivateRoute>} />
                                            <Route path="/finance" element={<PrivateRoute><Finance /></PrivateRoute>} />
                                            <Route path="/settings" element={<PrivateRoute><Settings /></PrivateRoute>} />
                                            <Route path="*" element={<Navigate to="/" replace />} />
                                        </Route>
                                    </Routes>
                                </Suspense>
                            </CoursesProvider>
                        </AlertSettingsProvider>
                    </ChildProvider>
                </I18nProvider>
            </AuthProvider>
        </Router>
        </AppErrorBoundary>
    );
}

export default App;
